import React, { useEffect, useState } from "react";
import DocumentsSignersGroup, {
  DealMemberSigner,
} from "./DocumentsSignersGroup";
import SignersPanelEmptyState from "./emptyState";
import "./styles/index.scss";
import * as api from "../../../../../api/generated-api";
import { UserWithPersonWithOrganizationPersons } from "../../../../../api/types";
import { Box } from "@mui/material";
import { DealLegalEntityWithInvestmentTypes } from "../../../dealDocuments/DealDocumentsPage/DealDocuments";

type Props = {
  orgId: number;
  user: UserWithPersonWithOrganizationPersons;
  dealId: number;
  dealDocumentId: number;
  dealLegalEntitiesWithInvestmentTypes: DealLegalEntityWithInvestmentTypes[];
  dealDocumentSignatures: api.DealDocumentSignature[];
  onAddSigners: (
    dealMemberSigners: DealMemberSigner[],
    documentId: number
  ) => void;
  onRemoveSigners: (
    dealMemberSigners: DealMemberSigner[],
    documentId: number
  ) => void;
  dealLegalEntitiesForUser: api.DealLegalEntity[];
  dealLawFirms: api.DealLawFirm[];
};

const SignatureAssignmentRightSidebar = (props: Props) => {
  const {
    orgId,
    user,
    dealId,
    dealDocumentId,
    dealDocumentSignatures,
    dealLegalEntitiesWithInvestmentTypes: dealLegalEntities,
    onAddSigners,
    onRemoveSigners,
    dealLegalEntitiesForUser,
  } = props;

  const [dealMemberSigners, setDealMemberSigners] = useState<
    DealMemberSigner[]
  >([]);

  useEffect(() => {
    const updatedDealMemberSigners: DealMemberSigner[] = dealLegalEntities.map(
      (dealLegalEntity) => {
        const dealDocumentSigner = dealDocumentSignatures.find(
          (dealDocumentSigner) =>
            dealDocumentSigner.dealLegalEntityId === dealLegalEntity.id
        );

        return {
          legalEntityType: dealLegalEntity.legalEntityType,
          title: dealLegalEntity.name,
          subTitle:
            dealLegalEntity.legalEntityType ===
              api.LegalEntityType.Investor_Investment ||
            dealLegalEntity.legalEntityType ===
              api.LegalEntityType.Company_Counsel
              ? `${dealLegalEntity.person.firstName} ${dealLegalEntity.person.lastName}`
              : dealLegalEntity.legalEntityType,
          signerId: dealDocumentSigner?.id,
          status: dealDocumentSigner?.status,
          dealLegalEntityId: dealLegalEntity.id,
          hasNewCashInvestment: dealLegalEntity.hasNewCashInvestment,
          hasSafeInvestment: dealLegalEntity.hasSafeInvestment,
        };
      }
    );

    // sort the investor and law firm signers by title
    const updatedDealMemberInvestorAndLawFirmSignersSorted =
      updatedDealMemberSigners
        .filter(
          (dealMemberSigner) =>
            dealMemberSigner.legalEntityType ===
              api.LegalEntityType.Investor_Investment ||
            dealMemberSigner.legalEntityType ===
              api.LegalEntityType.Company_Counsel
        )
        .sort((a, b) => {
          if (a.title < b.title) {
            return -1;
          }
          if (a.title > b.title) {
            return 1;
          }
          return 0;
        });

    const updatedDealMemberSignersSorted = [
      ...updatedDealMemberSigners.filter(
        (dealMemberSigner) =>
          dealMemberSigner.legalEntityType !==
            api.LegalEntityType.Investor_Investment &&
          dealMemberSigner.legalEntityType !==
            api.LegalEntityType.Company_Counsel
      ),
      ...updatedDealMemberInvestorAndLawFirmSignersSorted,
    ];

    setDealMemberSigners(updatedDealMemberSignersSorted);
  }, [dealDocumentSignatures, dealLegalEntities]);

  const DocumentsSignersGroupWrapper = ({
    groupTitle,
    groupMemberSigners,
  }: {
    groupTitle: string;
    groupMemberSigners: DealMemberSigner[];
  }) => {
    return (
      <DocumentsSignersGroup
        orgId={orgId}
        user={user}
        dealId={dealId}
        title={groupTitle}
        dealMemberSigners={groupMemberSigners}
        addSigners={(dealMemberSigners) =>
          onAddSigners(dealMemberSigners, dealDocumentId)
        }
        removeSigners={(dealMemberSigners) =>
          onRemoveSigners(dealMemberSigners, dealDocumentId)
        }
        dealLegalEntitiesForUser={dealLegalEntitiesForUser}
      />
    );
  };

  return (
    <Box mt={5}>
      {dealMemberSigners.length ? (
        <>
          <DocumentsSignersGroupWrapper
            groupTitle="Company Officers"
            groupMemberSigners={dealMemberSigners.filter(
              (dealCompanyMember) =>
                dealCompanyMember.legalEntityType ===
                  api.LegalEntityType.Chief_Executive_Officer ||
                dealCompanyMember.legalEntityType ===
                  api.LegalEntityType.Secretary
            )}
          />
          <DocumentsSignersGroupWrapper
            groupTitle="Key Common Holders"
            groupMemberSigners={dealMemberSigners.filter(
              (dealMemberSigner) =>
                dealMemberSigner.legalEntityType ===
                api.LegalEntityType.Key_Common_Holder
            )}
          />
          <DocumentsSignersGroupWrapper
            groupTitle="Board Members"
            groupMemberSigners={dealMemberSigners.filter(
              (dealMemberSigner) =>
                dealMemberSigner.legalEntityType ===
                api.LegalEntityType.Board_Member
            )}
          />
          <DocumentsSignersGroupWrapper
            groupTitle="Company Counsel"
            groupMemberSigners={dealMemberSigners.filter(
              (dealMemberSigner) =>
                dealMemberSigner.legalEntityType ===
                api.LegalEntityType.Company_Counsel
            )}
          />
          <DocumentsSignersGroupWrapper
            groupTitle="Investors (New Money)"
            groupMemberSigners={dealMemberSigners.filter(
              (dealMemberSigner) =>
                dealMemberSigner.legalEntityType ===
                  api.LegalEntityType.Investor_Investment &&
                dealMemberSigner.hasNewCashInvestment
            )}
          />
          <DocumentsSignersGroupWrapper
            groupTitle="Investors (SAFE Holders)"
            groupMemberSigners={dealMemberSigners.filter(
              (dealMemberSigner) =>
                dealMemberSigner.legalEntityType ===
                  api.LegalEntityType.Investor_Investment &&
                dealMemberSigner.hasSafeInvestment
            )}
          />
        </>
      ) : (
        <SignersPanelEmptyState orgId={orgId} dealId={dealId} />
      )}
    </Box>
  );
};

export default SignatureAssignmentRightSidebar;
